import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { seoHead } from "@/lib/seo";
import logoAsset from "@/assets/coffman-cove-logo.png.asset.json";
import { useHourlyReload, msUntilMinuteMark } from "@/lib/use-scheduled-reload";
import {
  fetchMarineWeather,
  fetchMarineConditions,
  fetchTides,
  type TidePrediction,
} from "@/lib/fishing-data";
import { getFishingReportFn, getTodayFishabilityFn } from "@/lib/marine.functions";
import { SafeAreaOverlay } from "@/components/SafeAreaOverlay";
import { DisplayBoardFrame } from "@/components/DisplayBoardFrame";
import { useLastKnownGood } from "@/lib/last-known-good";
import {
  ArrowDown,
  ArrowUp,
  Compass,
  Droplets,
  Gauge,
  Thermometer,
  Waves,
  Wind,
  type LucideIcon,
} from "lucide-react";


export const Route = createFileRoute("/display/weather")({
  validateSearch: (search: Record<string, unknown>) => ({
    safe:
      search.safe === "1" ||
      search.safe === 1 ||
      search.safe === true ||
      search.safe === "true",
  }),
  head: () =>
    seoHead({
      path: "/display/weather",
      title: "Weather Display Board — Coffman Cove",
      description:
        "Fixed 1080x1920 signage board for Coffman Cove, Alaska: today's fishability score, tides, wind, seas and temperature.",
    }),
  component: WeatherDisplay,
});

const TZ = "America/Anchorage";

/* Signage palette — the SITE brand palette, scaled for TV.
   Values mirror the navy/ember/sky tokens in src/styles.css so the board
   reads as the same brand as the website, not a generic black board. */
const BG_DEEP = "oklch(0.14 0.06 258)"; // --navy-deep
const BG_MID = "oklch(0.19 0.058 258)"; // between --navy-deep and --navy
const BOARD_BG = `linear-gradient(180deg, ${BG_DEEP} 0%, ${BG_MID} 55%, ${BG_DEEP} 100%)`;
const FG = "oklch(0.97 0.012 240)"; // --offwhite
const LABEL = "oklch(0.82 0.055 235)"; // sky-tinted label, ~10:1 on navy
const SCORE = "oklch(0.82 0.11 235)"; // --sky, brand highlight
const EMBER = "oklch(0.68 0.15 32)"; // --ember lifted for AA on navy
const RULE = "oklch(1 0 0 / 16%)"; // --border, TV weight
const CARD = "oklch(0.24 0.065 258)"; // --card
const CARD_BORDER = "oklch(1 0 0 / 14%)";

/* Brand type: Playfair Display for the wordmark/headings, Inter for data. */
const DISPLAY_FONT = '"Playfair Display", Georgia, serif';
const SANS_FONT =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';




/* Freshness gate: only blank the board once the snapshot is a full day old.
   A single missed refresh should degrade the "Updated X ago" pill, not wipe
   the TV. */
const STALE_LIMIT_MS = 24 * 60 * 60_000;
// How far past a tide extreme we still show it (marked approximate) when no
// future prediction is available.
const TIDE_GRACE_MS = 6 * 60 * 60_000;


function akTime(iso: string | number) {
  return new Date(iso).toLocaleTimeString("en-US", {
    timeZone: TZ,
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Full data timestamp: "Mon, Jul 27 · 11:42 PM AK" */
function akDateTime(iso: string | number) {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    timeZone: TZ,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return `${date} · ${akTime(iso)} AK`;
}

/** Human age of a timestamp, e.g. "just now", "18 min ago", "3 hr 5 min ago". */
function ageLabel(ms: number) {
  const mins = Math.max(0, Math.floor(ms / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs} hr ${rem} min ago` : `${hrs} hr ago`;
}

type TileStatus = { word: string; color: string; detail?: string };

/** Freshness tier drives the indicator colour and word. */

function freshnessFor(ageMs: number) {
  if (ageMs < 90 * 60_000) return { word: "Live", color: "oklch(0.78 0.16 150)" };
  if (ageMs < 6 * 60 * 60_000) return { word: "Recent", color: SCORE };
  return { word: "Stale", color: EMBER };
}


function verdictFor(score: number) {
  if (score >= 80) return "Prime";
  if (score >= 60) return "Good";
  if (score >= 40) return "Fair";
  if (score >= 20) return "Slow";
  return "Poor";
}

function WeatherDisplay() {
  const { safe } = Route.useSearch();
  // Refresh the board once an hour, on the :45 minute mark.
  useHourlyReload(45);

  // Ticking clock so the "updated X ago" indicator stays truthful between
  // hourly reloads. Mounted flag keeps SSR output stable (no hydration drift).
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    setNowTs(Date.now());
    const id = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);


  const q = {
    staleTime: 60 * 60_000,
    refetchInterval: () => msUntilMinuteMark(45),
    refetchOnWindowFocus: false,
  } as const;


  const weather = useQuery({
    queryKey: ["weather", "display"],
    queryFn: ({ signal }) => fetchMarineWeather(signal),
    ...q,
  });
  const marine = useQuery({
    queryKey: ["marine", "display"],
    queryFn: ({ signal }) => fetchMarineConditions(signal),
    ...q,
  });
  const tides = useQuery({
    queryKey: ["tides", "display"],
    queryFn: ({ signal }) => fetchTides(signal),
    ...q,
  });
  const report = useQuery({
    queryKey: ["fishing-report-narrative", "display"],
    queryFn: () => getFishingReportFn(),
    ...q,
  });
  const fishability = useQuery({
    queryKey: ["fishability-today", "display"],
    queryFn: () => getTodayFishabilityFn(),
    ...q,
  });

  // Last-known-good: during a brief API outage the queries error or return
  // null; fall back to the most recent valid payload we persisted so the board
  // keeps showing real conditions instead of blanking.
  const weatherLKG = useLastKnownGood("display:weather", weather.data);
  const marineLKG = useLastKnownGood("display:marine", marine.data);
  const tidesLKG = useLastKnownGood<TidePrediction[]>("display:tides", tides.data);
  const reportLKG = useLastKnownGood("display:report", report.data);
  const fishabilityLKG = useLastKnownGood("display:fishability", fishability.data);

  const weatherData = weatherLKG.data;
  const marineData = marineLKG.data;
  const tidesData = tidesLKG.data;
  const reportData = reportLKG.data;
  const fishabilityData = fishabilityLKG.data;

  // True when anything on the board is being served from the LKG cache.
  const servingCached =
    weatherLKG.fromCache ||
    marineLKG.fromCache ||
    tidesLKG.fromCache ||
    reportLKG.fromCache ||
    fishabilityLKG.fromCache;

  const current = weatherData?.current;
  const marineCurrent = marineData?.current;
  const reportRow = reportData?.report ?? null;

  const now = nowTs;
  const sortedTides: TidePrediction[] = [...(tidesData ?? [])].sort(
    (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime(),
  );
  const futureTides = sortedTides.filter((t) => new Date(t.t).getTime() > now);
  // Relaxed gating: when the prediction window has run out, fall back to the
  // most recent extremes within the grace window and flag them as approximate
  // rather than dropping the tide tiles entirely.
  const recentTides = sortedTides
    .filter((t) => {
      const ms = new Date(t.t).getTime();
      return ms <= now && now - ms <= TIDE_GRACE_MS;
    })
    .slice(-2);
  const tidesApproximate = futureTides.length === 0 && recentTides.length > 0;
  const upcoming: TidePrediction[] = (
    futureTides.length > 0 ? futureTides : recentTides
  ).slice(0, 2);

  // Per-source availability badges: each tile group reports whether its feed is
  // current, serving cached values, or missing — so partial outages read as
  // "this tile is stale" instead of blanking the board.
  const sourceStatus = (
    lkg: { data: unknown; fromCache: boolean; savedAt: number | null },
    fetchedAt: number,
  ): TileStatus => {
    if (lkg.data == null) return { word: "No data", color: EMBER };
    if (lkg.fromCache) {
      const age = lkg.savedAt != null ? now - lkg.savedAt : null;
      return {
        word: "Stale",
        color: EMBER,
        detail: age != null ? ageLabel(age) : undefined,
      };
    }
    const age = fetchedAt ? Math.max(0, now - fetchedAt) : 0;
    const f = freshnessFor(age);
    return { word: f.word, color: f.color, detail: ageLabel(age) };
  };

  const weatherStatus = sourceStatus(weatherLKG, weather.dataUpdatedAt);
  const marineStatus = sourceStatus(marineLKG, marine.dataUpdatedAt);
  const tidesStatus: TileStatus = tidesApproximate
    ? { word: "Approx", color: EMBER, detail: "latest available" }
    : sourceStatus(tidesLKG, tides.dataUpdatedAt);
  // Barometric pressure rides on the weather feed but is called out separately.
  const baroStatus: TileStatus =
    current?.pressure_msl == null
      ? { word: "No data", color: EMBER }
      : weatherStatus;


  // Freshness is driven by the most recent conditions snapshot.
  const snapshotIso = reportData?.lastUpdated ?? reportRow?.created_at ?? null;
  const snapshotMs = snapshotIso ? new Date(snapshotIso).getTime() : null;
  const snapshotFresh =
    snapshotMs != null && Number.isFinite(snapshotMs) && now - snapshotMs < STALE_LIMIT_MS;
  const updatedLabel = snapshotIso ? akDateTime(snapshotIso) : null;
  const snapshotAgeMs = snapshotMs != null ? Math.max(0, now - snapshotMs) : null;
  const freshness = snapshotAgeMs != null ? freshnessFor(snapshotAgeMs) : null;


  // Observation timestamp of the live weather reading itself (upstream time),
  // shown alongside the pipeline's snapshot time so both can be confirmed.
  const observedIso = current?.time ?? marineCurrent?.time ?? null;
  // Wall-clock render time of this board — proves the page itself is live.
  const renderedLabel = mounted ? akTime(nowTs) : null;


  // Core = the live weather reading. Tides are shown when available but must
  // never blank the whole board (the prediction window can run out late in the
  // day even though every other value is perfectly current).
  const hasCore =
    current?.temperature_2m != null && current?.wind_speed_10m != null;

  // Missing or >24h old conditions → single hero message, no cards, no dashes.
  const unavailable = !snapshotFresh || !hasCore;

  const score = fishabilityData?.score ?? null;
  const narrative = reportRow?.narrative ?? null;
  const showScore = !unavailable && score != null;

  const narrativeLines = (narrative ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 110)
    .replace(/\s+\S*$/, "");

  return (
    <DisplayBoardFrame>
    <div
      style={{
        width: 1080,
        height: 1920,
        background: BOARD_BG,
        color: FG,
        fontFamily: SANS_FONT,
        padding: "96px 54px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* 1. Brand lockup: logo, wordmark, tagline, last updated */}
      <div style={{ textAlign: "center" }}>
        <img
          src={logoAsset.url}
          alt="Coffman Cove Lodging"
          width={360}
          style={{ height: 180, width: "auto", margin: "0 auto 18px", display: "block" }}
        />
        <div
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 84,
            fontWeight: 700,
            lineHeight: 1.05,
          }}
        >
          Coffman Cove Lodging
        </div>
        <div
          style={{
            fontSize: 34,
            fontWeight: 600,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: EMBER,
            marginTop: 14,
          }}
        >
          Family owned · Prince of Wales Island, Alaska
        </div>
        {/* Data timestamp + live freshness indicator */}
        <div
          style={{
            marginTop: 18,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 14,
              background: CARD,
              border: `3px solid ${freshness ? freshness.color : CARD_BORDER}`,
              borderRadius: 999,
              padding: "10px 28px",
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: freshness ? freshness.color : LABEL,
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: 999,
                background: freshness ? freshness.color : LABEL,
                display: "inline-block",
              }}
              aria-hidden
            />
            {freshness ? freshness.word : "No data"}
          </span>
          <span
            style={{
              fontSize: 34,
              fontWeight: 600,
              color: LABEL,
              whiteSpace: "nowrap",
            }}
          >
            {mounted && snapshotAgeMs != null
              ? `Updated ${ageLabel(snapshotAgeMs)}`
              : "Update age unknown"}
          </span>
          {mounted && servingCached ? (
            <span
              style={{
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: EMBER,
                border: `3px solid ${EMBER}`,
                borderRadius: 999,
                padding: "8px 24px",
                whiteSpace: "nowrap",
              }}
            >
              Last known good
            </span>
          ) : null}
        </div>

        <div
          style={{
            fontSize: 38,
            fontWeight: 500,
            color: FG,
            marginTop: 12,
            whiteSpace: "nowrap",
          }}
        >
          {updatedLabel ? `Data as of ${updatedLabel}` : "Update time unavailable"}
        </div>

      </div>




      <div style={{ height: 3, background: RULE, margin: "28px 0" }} />

      {unavailable ? (
        /* 2. Empty state: one centered hero message, nothing else. */
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 132, fontWeight: 700, lineHeight: 1.05 }}>
            Conditions
            <br />
            Unavailable
          </div>
          <div style={{ fontSize: 48, fontWeight: 500, color: LABEL, marginTop: 40 }}>
            {updatedLabel
              ? `Last known update ${updatedLabel}`
              : "No recent update on record"}
          </div>
        </div>
      ) : (
        <>
          {/* Hero: fishability score + verdict */}
          {showScore && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, fontWeight: 600, color: LABEL }}>
                Today's fishability
              </div>
              <div
                style={{
                  fontSize: 240,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: SCORE,
                  fontVariantNumeric: "tabular-nums",
                  marginTop: 8,
                }}
              >
                {Math.round(score!)}
              </div>
              <div style={{ fontSize: 84, fontWeight: 600, marginTop: 4 }}>
                {verdictFor(score!)}
              </div>
            </div>
          )}

          {showScore && (
            <div style={{ height: 3, background: RULE, margin: "28px 0" }} />
          )}

          {/* 3. Widget tiles — same look as the /weather widgets, TV scale */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 24,
            }}
          >
            <Tile
              label="Air"
              icon={Thermometer}
              value={`${Math.round(current!.temperature_2m!)}°F`}
              status={weatherStatus}
            />
            <Tile
              label="Wind"
              icon={Wind}
              value={`${Math.round(current!.wind_speed_10m!)} kn`}
              status={weatherStatus}
            />
            <Tile
              label="Pressure"
              icon={Gauge}
              value={
                current?.pressure_msl != null
                  ? `${Math.round(current.pressure_msl)}`
                  : "N/A"
              }
              unit="hPa"
              status={baroStatus}
            />
            <Tile
              label="Seas"
              icon={Waves}
              value={
                marineCurrent?.wave_height != null
                  ? `${marineCurrent.wave_height.toFixed(1)} ft`
                  : "N/A"
              }
              status={marineStatus}

            />
          </div>

          <div style={{ height: 24 }} />

          {/* 4. Second tile row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 24,
            }}
          >
            {upcoming[0] && (
              <Tile
                label={
                  tidesApproximate
                    ? upcoming[0].type === "H"
                      ? "Last high"
                      : "Last low"
                    : upcoming[0].type === "H"
                      ? "Next high"
                      : "Next low"
                }
                icon={upcoming[0].type === "H" ? ArrowUp : ArrowDown}
                value={akTime(upcoming[0].t)}
                note={tidesApproximate ? "Approximate" : undefined}
                status={tidesStatus}
                accent
              />
            )}
            {upcoming[1] && (
              <Tile
                label={
                  tidesApproximate
                    ? upcoming[1].type === "H"
                      ? "Prior high"
                      : "Prior low"
                    : upcoming[1].type === "H"
                      ? "Then high"
                      : "Then low"
                }
                icon={upcoming[1].type === "H" ? ArrowUp : ArrowDown}
                value={akTime(upcoming[1].t)}
                note={tidesApproximate ? "Approximate" : undefined}
                status={tidesStatus}
                accent
              />
            )}

            <Tile
              label="Water temp"
              icon={Droplets}
              value={
                marineCurrent?.sea_surface_temperature != null
                  ? `${Math.round(marineCurrent.sea_surface_temperature)}°F`
                  : "N/A"
              }
              status={marineStatus}
            />
            <Tile
              label="Gusts"
              icon={Compass}
              value={
                current?.wind_gusts_10m != null
                  ? `${Math.round(current.wind_gusts_10m)} kn`
                  : "N/A"
              }
              status={weatherStatus}
            />

          </div>


          {narrativeLines && (
            <div
              style={{
                marginTop: 26,
                marginBottom: 6,
                fontSize: 38,
                fontWeight: 500,
                lineHeight: 1.35,
                color: FG,
                textAlign: "center",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {narrativeLines}
            </div>
          )}

        </>
      )}

      {/* Branded footer bar */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 26,
          borderTop: `3px solid ${RULE}`,
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        <span
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: 40,
            fontWeight: 700,
            color: FG,
            whiteSpace: "nowrap",
          }}
        >
          coffmancovelodging.com
        </span>
        <span
          style={{
            fontSize: 26,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: LABEL,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {observedIso ? `Obs ${akTime(observedIso)}` : "Obs n/a"}
          {renderedLabel ? ` · ${renderedLabel}` : ""} AK
        </span>

      </div>

      {safe ? <SafeAreaOverlay /> : null}
    </div>
    </DisplayBoardFrame>
  );
}

/**
 * TV-scale version of the /weather page widget: same rounded, bordered,
 * translucent card with an uppercase tracked label and icon — just sized for
 * a 1080x1920 board.
 */
function Tile({
  label,
  icon: Icon,
  value,
  unit,
  note,
  status,
  accent = false,
}: {
  label: string;
  icon: LucideIcon;
  value: string;
  unit?: string;
  note?: string;
  status?: TileStatus;
  accent?: boolean;
}) {

  return (
    <div
      style={{
        background: CARD,
        border: `3px solid ${accent ? "oklch(0.68 0.15 32 / 45%)" : CARD_BORDER}`,
        borderRadius: 28,
        padding: "24px 30px 28px",
        boxShadow: "0 12px 34px -18px oklch(0 0 0 / 70%)",
        minWidth: 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          marginBottom: 10,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: accent ? EMBER : LABEL,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          <Icon size={30} strokeWidth={2.5} aria-hidden />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {label}
          </span>
        </div>
        {status && (
          <span
            title={status.detail}
            style={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 16px",
              borderRadius: 999,
              border: `2px solid ${status.color}`,
              color: status.color,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 999,
                background: status.color,
                display: "inline-block",
              }}
              aria-hidden
            />
            {status.word}
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          fontSize: 88,
          fontWeight: 700,
          lineHeight: 1.05,
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
        {unit && (
          <span style={{ fontSize: 40, fontWeight: 600, color: LABEL }}>{unit}</span>
        )}
      </div>
      {note && (
        <div
          style={{
            marginTop: 8,
            fontSize: 30,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: LABEL,
          }}
        >
          ~ {note}
        </div>
      )}
    </div>

  );
}
